// Content for the /travel-credit-card/comparison page, transcribed from the
// Figma frame (CBCC 2026, node 2875-9180). Fully static and SSR'd — long-form
// SEO copy, organised section-wise.
//
// Known design-source fixes applied deliberately (the Figma export had run-
// together text in places where line breaks were lost):
//  - "BENEFITST" heading typo -> "BENEFITS"
//  - International-savings callout: re-punctuated the merged sentence into a
//    readable "Typical card …; ixigo … Saving: …" form.
//  - Protection table row mislabelled "International Acceptance" -> "Offers"

export const COMPARISON_SEO = {
	title: 'Compare VISA and RuPay Cards — ixigo AU Credit Card',
	description:
		'Compare the VISA and RuPay variants of the ixigo AU Credit Card: rewards, lounge access, UPI features, forex, fees, eligibility, and how to apply.',
	canonicalUrl: 'https://www.ixigo.com/travel-credit-card/comparison',
} as const;

// A table cell is either plain text or a link ({ text, href }) — the latter
// renders as an external anchor (e.g. the VISA/RuPay "Offers" row).
export type ComparisonCell = string | { text: string; href: string };

export type ComparisonBlock =
	| { type: 'p'; text: string }
	| { type: 'h3'; text: string }
	// list items auto-bold the lead phrase before the first " — " when rendered
	| { type: 'list'; items: string[] }
	| { type: 'note'; text: string }
	| { type: 'callout'; title: string; text: string }
	| {
			type: 'table';
			caption?: string;
			columns: string[];
			groups: { title?: string; rows: ComparisonCell[][] }[];
	  };

export interface ComparisonSection {
	id: string;
	title: string;
	blocks: ComparisonBlock[];
}

export const COMPARISON_CONTENT: ComparisonSection[] = [
	{
		id: 'compare-visa-rupay',
		title: 'Compare VISA and RuPay Card',
		blocks: [
			{
				type: 'table',
				columns: ['Features', 'Visa Card', 'Rupay Card'],
				groups: [
					{
						title: 'Welcome Reward Points',
						rows: [
							['Welcome Reward Points', '1,000 Points', '1,000 Points'],
							['Welcome ixigo Money', '₹1,000', '₹1,000'],
						],
					},
					{
						title: 'Reward Points & Earnings',
						rows: [
							['Online Spends (per ₹200)', '5 Points', '5 Points'],
							['Offline Spends (per ₹200)', '5 Points', '5 Points'],
							[
								'Online UPI Transactions (per ₹200)',
								'Not Supported',
								'5 Points',
							],
							[
								'Offline UPI Transactions (per ₹200)',
								'Not Supported',
								'5 Points',
							],
							[
								'Train Bookings via ixigo',
								'2.5% Reward Points',
								'2.5% Reward Points',
							],
							[
								'Quarterly Spend Bonus (₹75,000+)',
								'5,000 Points',
								'5,000 Points',
							],
							['Monthly Reward Points Cap', '10,000 Points', '10,000 Points'],
						],
					},
					{
						title: 'Travel Benefits & Discounts',
						rows: [
							[
								'Flight Bookings on ixigo',
								'10% Off (up to ₹3,000/month)',
								'10% Off (up to ₹3,000/month)',
							],
							[
								'Hotel Bookings on ixigo',
								'10% Off (up to ₹3,000/month)',
								'10% Off (up to ₹3,000/month)',
							],
							[
								'Bus Bookings on ixigo',
								'10% Off (up to ₹300/month)',
								'10% Off (up to ₹300/month)',
							],
							[
								'Train Payment Gateway Fee Waiver',
								'2x per Month (ixigo Trains) + 2x per Month (Confirmtkt)',
								'2x per Month (ixigo Trains) + 2x per Month (Confirmtkt)',
							],
						],
					},
					{
						title: 'Lounge Access',
						rows: [
							[
								'Domestic Airport Lounges',
								'8 per Year (2/Quarter)',
								'8 per Year (2/Quarter)',
							],
							['Railway Lounges', '8 per Year', '8 per Year'],
							[
								'International Lounge Access (Priority Pass)',
								'1 per Year',
								'1 per Year',
							],
						],
					},
					{
						title: 'Payments & UPI Features',
						rows: [
							['UPI Payments Support', 'Not Available', 'Supported'],
							[
								'Contactless Payments (Tap to Pay)',
								'Supported up to ₹5,000',
								'Supported up to ₹5,000',
							],
							['Fuel Surcharge Waiver', '1% Waiver', '1% Waiver'],
						],
					},
					{
						title: 'Global Usage & Forex',
						rows: [
							['Zero Forex Markup', 'Yes', 'Yes'],
							[
								'International Acceptance',
								'Global Acceptance',
								'Limited International Acceptance',
							],
						],
					},
					{
						title: 'Protection & Additional Benefits',
						rows: [
							['Insurance Cover', 'Not Available', 'Up to ₹2 Lakh'],
							[
								'Offers',
								{
									text: 'VISA Offers',
									href: 'https://www.visa.co.in/en_in/visa-offers-and-perks/',
								},
								{
									text: 'NPCI / RuPay Offers',
									href: 'https://www.rupay.co.in/domestic-offers',
								},
							],
						],
					},
				],
			},
		],
	},
	{
		id: 'why-choose',
		title: 'Why Choose the ixigo AU Credit Card for Travel?',
		blocks: [
			{
				type: 'p',
				text: 'Travellers looking for the best travel credit card in India often face a common trade-off: either pay a steep annual fee for premium perks or settle for a basic card with limited rewards. The ixigo AU Credit Card is built around a different idea - a travel rewards credit card with no joining fee, no annual fee, and a reward structure that actually works for the way most Indians travel.',
			},
			{
				type: 'p',
				text: 'Here is what makes it stand out among the top-rated travel credit cards available today:',
			},
			{
				type: 'list',
				items: [
					'Zero fee for life: No joining fee and no renewal fee. Unlike most travel credit cards that waive the first-year fee and charge ₹1,000–₹5,000 from year two, the ixigo AU Credit Card is genuinely lifetime free.',
					'Both airport and railway lounge access: Most credit cards cover only airport lounges. This card also includes railway lounge access, which is a practical benefit for train travellers across India.',
					'Earn travel rewards on UPI payments: A feature that is uncommon among co-branded travel cards. When you link the RuPay variant to any UPI app and make payments via QR code scan, you continue to earn credit card reward points.',
					'Zero forex markup fee on international spends: Ideal for those looking for a good credit card for international travel. A typical card charges 3.5% on every foreign currency transaction; this card charges zero, resulting in real savings every time you swipe abroad',
					'ixigo co-branded advantage: As a co-branded credit card, offers additional discounts and ixigo money on bookings made through the ixigo platform for flights, hotels, trains, and buses.',
				],
			},
		],
	},
	{
		id: 'what-is',
		title: 'What is ixigo AU Credit Card?',
		blocks: [
			{
				type: 'p',
				text: "The ixigo AU Credit Card is a co-branded credit card - a type of card jointly issued by a financial institution and a brand partner to offer cardholders benefits specific to that partner's platform. In this case, AU Small Finance Bank (the card issuer and an RBI-regulated scheduled commercial bank) and ixigo (India's leading travel platform) have partnered to create a card designed around how Indians book and pay for travel.",
			},
			{
				type: 'p',
				text: "Co-branded credit cards typically offer 2–3x more value on the partner platform's services compared to a standard credit card. For the ixigo AU Credit Card, this means additional discounts and ixigo money credits when you book flights, hotels, trains, or buses through ixigo - on top of the base reward points you earn on all other spends.",
			},
			{ type: 'p', text: 'The card comes in two variants:' },
			{
				type: 'list',
				items: [
					'Visa variant: suited for those who travel abroad and want zero forex markup on foreign currency transactions, along with international airport lounge access via Priority Pass.',
					'RuPay variant:  suited for domestic travellers who want to link the card with UPI apps (PhonePe, BHIM, GPay) to earn credit card rewards on UPI payments made via QR code scans, plus railway lounge access and a ₹2 lakh travel insurance.',
				],
			},
			{
				type: 'p',
				text: 'Both variants are issued by AU Small Finance Bank, which has been operating as a full-service scheduled commercial bank since 2017 and is regulated by the Reserve Bank of India. For those unfamiliar with AU Bank, it is not a new entrant to the banking sector.  The bank has a widespread branch and ATM network across India.',
			},
		],
	},
	{
		id: 'benefits',
		title: 'Card Benefits',
		blocks: [
			{
				type: 'p',
				text: 'The card is structured around benefits that add up over time rather than one-off perks. Here is a complete breakdown of what you get, along with the actual value in rupee terms.',
			},
			{ type: 'h3', text: 'Welcome Benefit' },
			{
				type: 'p',
				text: 'On your first successful transaction within 30 days of card issuance, you receive:',
			},
			{
				type: 'list',
				items: [
					'1,000 reward points',
					'₹1,000 in ixigo money, credited directly to your ixigo account for use on flights, hotels, trains, or buses',
				],
			},
			{
				type: 'table',
				caption: 'Reward Points — Earning Structure',
				columns: [],
				groups: [
					{
						rows: [
							['Online spends (including ixigo bookings)', '5 points/₹200'],
							['CC linked UPI transactions (RuPay)', '5 points/₹200'],
							['Offline / POS spends', '5 points/₹200'],
							[
								'Milestone Bonus (₹75K spend in a quarter)',
								'Bonus points credited*',
							],
						],
					},
				],
			},
			{
				type: 'note',
				text: 'Points not earned on fuel, rent, wallet, and jewellery transactions.',
			},
		],
	},
	{
		id: 'real-spend',
		title: 'Other Card Benefits',
		blocks: [
			{
				type: 'list',
				items: [
					'Fuel Surcharge Waiver: 1% fuel surcharge waiver of up to ₹250 per month.',
					'UPI-linked rewards: Only available on the RuPay variant; link your card to any UPI app and earn 3 credit card reward points for every ₹200 spent via UPI QR scans. This is one of the most distinctive features among flight credit cards and travel cards in India.',
					'RuPay Travel Accident Cover: ₹2 lakh insurance cover for accidental death while travelling, available on the RuPay variant.',
					'24/7 Concierge Services: available on the RuPay variant for assistance with travel bookings, reservations, and information.',
				],
			},
		],
	},
	{
		id: 'co-branded-benefits',
		title: 'ixigo Specific Card Benefits',
		blocks: [
			{
				type: 'p',
				text: 'The ixigo AU Credit Card is not just a general travel rewards credit card  it is a co-branded card, which means some benefits are exclusive to the ixigo platform. This is one of the most direct financial advantages of the ixigo-AU Bank partnership.',
			},
			{
				type: 'p',
				text: 'When you book through ixigo using this card, you get:',
			},
			{
				type: 'list',
				items: [
					'ixigo money credits on eligible bookings: ixigo money can be redeemed  only on the ixigo platform for flight tickets, hotel bookings, train tickets, and bus tickets.',
					'Discount on flights, hotels, and buses: co-branded cardholders get platform-specific offers that are not available through other payment methods. Check the current offers section on ixigo for LIVE deals.',
					'Payment gateway charges waived on train bookings: ixigo AU Credit Card holders do not pay the payment gateway fee on IRCTC train ticket bookings made via the ixigo app.',
					'Broader booking coverage: the co-branded benefits apply across all four modes: domestic and international flights, hotels (budget to premium), trains, and intercity buses.',
				],
			},
			{
				type: 'callout',
				title: 'Did you know?',
				text: 'Co-branded credit cards typically offer 2–3x more value on spends made through the partner platform compared to a generic travel card. For frequent ixigo users, the combination of ixigo money credits, booking discounts, and reward points on flights and hotels makes this a significantly more rewarding card than a standard travel credit card from a standalone bank.',
			},
			{ type: 'h3', text: 'Can I use this card on other travel platforms?' },
			{
				type: 'p',
				text: 'Yes. The ixigo AU Credit Card works like any other Visa or RuPay card. You can use it on any travel platform, e-commerce site, or for in-store purchase. The standard reward points apply everywhere. The additional ixigo-specific discounts and ixigo money credits apply only when you book through the ixigo platform. It is not a restricted card.',
			},
		],
	},
	{
		id: 'lounge-access',
		title: 'Airport and Railway Lounge Access',
		blocks: [
			{
				type: 'p',
				text: 'One of the key reasons travellers look for airport lounge access credit cards is the ability to wait in comfort between flights without paying out-of-pocket for day passes. The ixigo AU Credit Card provides both domestic airport lounge access and railway lounge access - a combination that few travel credit cards in India offer without an annual fee.',
			},
			{ type: 'h3', text: 'Domestic Airport Lounge Access' },
			{
				type: 'p',
				text: 'Cardholders can access domestic airport lounges across India — up to 8 complimentary visits per year. Lounge visits are subject to a minimum quarterly spend threshold; you need to meet the spend requirement in the preceding quarter to unlock lounge access in the current quarter.',
			},
			{
				type: 'p',
				text: 'Accessible lounges are available at all domestic airports in India',
			},
			{ type: 'h3', text: 'Railway Lounge Access' },
			{
				type: 'p',
				text: 'India is a country where train travel remains an essential mode of transportation, and railway lounge access is a benefit that most airline credit cards skip entirely.The ixigo AU Credit Card covers railway lounge access at select major railway stations. This is particularly useful for long-distance travellers who often spend extended periods at major railway junctions.',
			},
			{ type: 'h3', text: 'International Lounge Access' },
			{
				type: 'p',
				text: 'For the Visa variant, cardholders get 1 complimentary international airport lounge visit per year via Priority Pass — one of the most widely recognised lounge access programmes globally, covering 1,300+ lounges in 140+ countries. This benefit is particularly relevant for occasional international travellers who want a credit card for international travel without paying a premium annual fee.',
			},
			{
				type: 'p',
				text: 'To use Priority Pass, the membership is linked to your card.  You need to present your credit card and a Priority Pass member card at participating international lounges. Lounge availability varies by airport.',
			},
			{
				type: 'callout',
				title: 'FAQ: What is the minimum quarterly spend to get lounge access?',
				text: 'Lounge access (domestic airport and railway) is unlocked by meeting the minimum quarterly spend requirement in the preceding quarter. Please refer to the latest T&Cs on the AU Bank website or the ixigo credit card section for the exact spend threshold, as it is subject to periodic revision.',
			},
		],
	},
	{
		id: 'using-internationally',
		title: 'Using the ixigo AU Credit Card Internationally',
		blocks: [
			{
				type: 'p',
				text: 'For those using their card abroad, the forex markup fee is the single biggest hidden cost of most credit cards. A forex markup is the additional percentage a bank charges when you spend in a foreign currency - typically 3% to 3.5% on most Indian credit cards, plus GST.',
			},
			{
				type: 'p',
				text: 'The ixigo AU Credit Card (Visa variant) charges 0% forex markup. On ₹50,000 of international spending, a typical credit card would cost you ₹1,750 extra in forex markup charges. With the ixigo AU Credit Card, that savings stays in your pocket.',
			},
			{
				type: 'callout',
				title: 'International spend savings — quick illustration',
				text: 'Spend ₹50,000 on a trip abroad → Typical card (3.5% markup): ₹1,750 in charges; ixigo AU Visa Card (0% markup): ₹0. Saving: ₹1,750 on a single trip.',
			},
			{ type: 'h3', text: 'Additional International Benefits (Visa Variant)' },
			{
				type: 'list',
				items: [
					'Accepted at all Visa-enabled merchants globally - hotels, restaurants, retail stores, transport.',
					'1 Priority Pass international lounge visit per year - useful at busy international transit airports.',
					'3D Secure authentication for safe international online transactions.',
				],
			},
		],
	},
	{
		id: 'rewards-how-it-works',
		title: 'How Reward Points Work',
		blocks: [
			{
				type: 'p',
				text: 'Understanding how credit card reward points work before you apply prevents disappointment later. Here is a clear breakdown of how the ixigo AU Credit Card rewards system is structured — from earning to redemption.',
			},
			{
				type: 'table',
				caption: 'Earning Reward Points',
				columns: ['Spend Type', 'Points per ₹200'],
				groups: [
					{
						rows: [
							['Online spends (all platforms)', '5'],
							['UPI via linked RuPay card', '5'],
							['Offline / in-store POS spends', '5'],
							['Government payments / tax', '0 (excluded)'],
							[
								'Fuel transactions',
								'0 (excluded from points; surcharge waiver applies)',
							],
							['EMI transactions', '0 (excluded)'],
						],
					},
				],
			},
			{ type: 'h3', text: 'Milestone Bonus' },
			{
				type: 'p',
				text: 'If your card spending crosses the quarterly milestone threshold (check current T&Cs for the exact amount, typically in the range of ₹75,000 per quarter), you receive bonus reward points credited to your account at the end of the quarter. This accelerates the overall value you extract from the card for moderate-to-high spenders',
			},
			{ type: 'h3', text: 'Redeeming ixigo Money' },
			{
				type: 'p',
				text: 'Reward points can be converted to ixigo money and redeemed on the ixigo platform. ixigo money is valid for:',
			},
			{
				type: 'list',
				items: [
					'Domestic and international flight bookings on ixigo',
					'Hotel bookings (budget to premium) on ixigo',
					'IRCTC train ticket bookings on the ixigo app',
					'Intercity bus bookings on ixigo',
				],
			},
			{ type: 'h3', text: 'Validity of Reward Points' },
			{
				type: 'p',
				text: 'Reward points have a validity period-  check the current T&Cs on the AU Bank website for the expiry timeline, as it is subject to the card agreement terms. It is advisable to redeem points before they lapse, particularly if you use the card infrequently.',
			},
			{ type: 'h3', text: 'Transactions That Do Not Earn Reward Points' },
			{
				type: 'p',
				text: 'In the interest of transparency: reward points are not earned on fuel transactions (though the surcharge waiver applies), government utility payments, rent payments through third-party apps, EMI conversions, and cash advance transactions. These exclusions are standard across most credit card reward programmes in India.',
			},
		],
	},
	{
		id: 'charges-eligibility',
		title: 'ixigo AU Credit Card Charges, Eligibility and Required Documents',
		blocks: [
			{
				type: 'p',
				text: 'Before you apply for any credit card, it is worth understanding the full fee structure. The ixigo AU Credit Card is transparent on charges - here is the complete picture.',
			},
			{
				type: 'table',
				caption: 'Fee & Charges Table',
				columns: ['Fee Type', 'Visa Card', 'Rupay Card'],
				groups: [
					{
						rows: [
							['Joining Fee', '₹0', '₹0'],
							[
								'Annual / Renewal Fee',
								'₹0 — Lifetime Free',
								'₹0 — Lifetime Free',
							],
							['Add-on Card Fee', '₹0', '₹0'],
							['Forex Markup Fee', '0%', 'N/A (domestic card)'],
							[
								'Cash Advance Fee',
								'2.5% of amount (min ₹500)',
								'2.5% of amount (min ₹500)',
							],
							[
								'Late Payment Fee',
								'Slab-based on outstanding — check current T&C',
								'Slab-based on outstanding — check current T&C',
							],
							[
								'Over-Limit Fee',
								'2.5% of over-limit amount (min ₹500)',
								'2.5% of over-limit amount (min ₹500)',
							],
							[
								'Finance Charges (revolving credit)',
								'Up to 3.49% per month',
								'Up to 3.49% per month',
							],
						],
					},
				],
			},
			{
				type: 'note',
				text: 'All charges are subject to applicable GST. Verify the latest fee schedule on the AU Bank website before applying.',
			},
			{ type: 'h3', text: 'Eligibility Criteria' },
			{
				type: 'p',
				text: 'To apply for the AU ixigo Credit Card, you need to meet the following criteria:',
			},
			{
				type: 'list',
				items: [
					'Age: 21 to 60 years for primary cardholders; up to 70 years for senior applicants',
					'Employment type: Salaried or self-employed',
					'Minimum income: Confirm the current income requirement with AU Bank at the time of application, as thresholds may vary by profile',
					'CIBIL score: 700 or above is generally recommended for a smoother application; a lower score does not automatically disqualify you, but may affect approval',
					'Residential status: Indian resident',
				],
			},
			{ type: 'h3', text: 'Documents Required' },
			{
				type: 'p',
				text: 'The application process is paperless and online. You will need:',
			},
			{
				type: 'list',
				items: [
					'PAN card (mandatory)',
					'Aadhaar card (for KYC)',
					'Income proof: Latest salary slip (last 1–3 months) for salaried applicants; latest ITR for self-employed applicants.',
					'Passport-size photograph',
					'Address proof — Aadhaar is typically accepted',
				],
			},
			{
				type: 'callout',
				title: 'Do I need an AU Bank account to apply?',
				text: 'No. You do not need an existing AU Small Finance Bank savings or current account to apply for the ixigo AU Credit Card. The application is open to all eligible Indian residents regardless of their primary banking relationship.',
			},
		],
	},
	{
		id: 'how-to-apply',
		title: 'How to Apply for the AU ixigo Credit Card Online',
		blocks: [
			{
				type: 'p',
				text: 'Applying for the ixigo AU Credit Card takes less than 5 minutes. The entire process is online - no branch visit required. Here is how it works:',
			},
			{
				type: 'list',
				items: [
					'Visit the ixigo AU Credit Card page on ixigo.com or open the ixigo app on your phone.',
					"Click 'Apply Now' and choose your preferred card variant - Visa (for international use) or RuPay (for UPI rewards and domestic travel).",
					'Fill in the application form with your basic details: name, PAN, date of birth, mobile number, income details, and employment type. The form is short and typically takes 2–3 minutes.',
					'Complete the KYC process - submit your Aadhaar for digital verification (Aadhaar-OTP based; no physical documents required at this stage).',
					'Upload income proof if prompted - salary slip or ITR depending on your employment type.',
					'Submit the application. You will receive an acknowledgement and application reference number on your registered mobile number and email address.',
					'Once approved, the card is dispatched and typically delivered within 5–7 business days to your registered address.',
				],
			},
			{
				type: 'p',
				text: 'You can also apply directly via the ixigo app - the application flow is identical, optimised for mobile.',
			},
			{
				type: 'callout',
				title: 'Application Timeline',
				text: 'Form filling: ~3 minutes | KYC verification: same day | Approval decision: typically within 24–48 hours | Card delivery: 5–7 business days',
			},
		],
	},
];

// FAQ accordion shown at the END of the comparison page. `answer` is
// product-authored HTML (lists, links, bold) rendered via
// dangerouslySetInnerHTML in the page — static content, no user input.
export interface ComparisonFaq {
	question: string;
	/** trusted static HTML string */
	answer: string;
}

export const COMPARISON_FAQ: ComparisonFaq[] = [
	{
		question: 'Q. What is the ixigo AU Credit Card?',
		answer:
			'The ixigo AU Credit Card is a co-branded travel credit card launched by ixigo in partnership with AU Small Finance Bank. You can apply for this lifetime free credit card to save on your travel bookings and earn reward points on online and offline transactions, including spends on ixigo. This travel rewards card is available in both RuPay and Visa variants and you can choose either one. For detailed information, please refer to our Terms and Conditions',
	},
	{
		question:
			'Q. What makes the ixigo AU Credit Card the best credit card in India?',
		answer:
			'<div><p style="margin: 0 0 5px 0">Customers can enjoy the following travel benefits with ixigo AU Credit Card -</p>     <ul style="padding:0;margin:0">         <li style="list-style: disc ; margin-left: 12px">Instant discounts on flights, buses and hotel bookings made via ixigo (applicable once a month)</li>         <li style="list-style: disc ; margin-left: 12px">Zero PG fee for train bookings on ixigo (applicable twice a month).</li>         <li style="list-style: disc ; margin-left: 12px">16 railway and airport lounges + 1 international airport lounge access per year</li>         <li style="list-style: disc ; margin-left: 12px">1% fuel surcharge waiver of up to ₹250 per month for road expeditions</li> <li style="list-style: disc ; margin-left: 12px">5 reward points for every ₹200 spent offline and 5 reward points for every ₹200 spent online, including flight, hotel & bus bookings via ixigo.</li>    </ul><br><p>These benefits are applicable for both Visa and RuPay variants, making it one of the best travel credit cards available. Cardholders are eligible for 2 complimentary domestic airport lounge access per calendar quarter upon spending ₹50,000 or more during that quarter.</p></div>',
	},
	{
		question:
			'Q. What are the various benefits of my lifetime free credit card?',
		answer:
			"<div>The key benefits on your ixigo AU travel credit card include instant discounts on flight, bus and hotel bookings once per month. Payment gateway charges are waived off for train bookings twice per month.<br><br>As a welcome benefit, cardholders receive ₹1000 in ixigo money and 1000 reward points. These reward points can be converted into 500 ixigo money (1 reward point = 0.50 ixigo money). The ixigo money is credited to the cardholder's ixigo money account within 15 working days of the first transaction and the reward points are credited within 7 working days.<br><br>All these benefits are applicable on both Visa and RuPay variants of the credit card, making it the best lifetime free credit card with essential travel benefits.</div>",
	},
	{
		question: 'Q.  Who can apply for the ixigo AU Credit Card?',
		answer:
			'<div><p style="margin: 0 0 5px 0">To be eligible for the ixigo AU travel credit card in India, the applicant must:</p>     <ul style="padding:0;margin:0">         <li style="list-style: disc ; margin-left: 12px">Be a registered user of any of the ixigo apps.</li>         <li style="list-style: disc ; margin-left: 12px">Meet the age requirement: 21 to 60 years old.</li>         <li style="list-style: disc ; margin-left: 12px">Be an Indian resident.</li>     </ul> </div>',
	},
	{
		question:
			'Q. I already have an AU Small Finance BankCredit Card. Can I still apply?',
		answer:
			'Currently, the ixigo AU Credit Card is only available for new AU Bank Credit Card customers. However, we are actively working on enabling it for existing customers as well.',
	},
	{
		question:
			'Q. I already have an ixigo AU Visa Credit Card. Can I apply for the ixigo AU RuPay Credit Card?',
		answer:
			'No, existing ixigo AU Visa Credit Cardholders cannot apply for the RuPay variant of the credit card.',
	},
	{
		question:
			'Q. What is the validity of ixigo money earned with the travel credit card?',
		answer:
			'ixigo money will be credited to your ixigo money account within 15 working days of the 1st transaction. An email will be sent to your registered email ID once the amount is credited. It will remain valid for 90 days from the date of credit.',
	},
	{
		question: 'Q. How do I place the request for a Priority Pass?',
		answer:
			'<div>You can call 1800 1200 1500 to request a priority pass or send an email from your registered email ID to <a style="color: blue ; text-decoration: underline" target="_blank" href="mailto:creditcard.support@aubank.in">creditcard.support@aubank.in</a>.</div>',
	},
	{
		question: 'Q. How can I get access to a Priority Pass Lounge?',
		answer:
			'<div>You must present your Priority Pass card, issued to you along with your Credit Card, at the lounge to gain access. Please note that charges of $27 + taxes are applicable in the following scenarios:<br><br> <ul><li style="list-style: disc ; margin-left: 12px">Visits to lounges in India</li> <li style="list-style: disc ; margin-left: 12px">Visits to international airport lounges (Outside India) beyond the allowed complimentary visits</li> <li style="list-style: disc ; margin-left: 12px">For any guest visits</li></ul></div>',
	},
	{
		question: 'Q. What is included in retail spends?',
		answer:
			'Retail spends include POS (both contactless and chip + PIN) and e-commerce/online transactions with the exclusion of other transactions like loan amounts, cash withdrawals, balance transfers or any fees and charges, including GST.',
	},
	{
		question: 'Q. Is there any Annual fee applicable on my Credit Card?',
		answer:
			'No, this travel credit card is Lifetime Free and there is no joining or annual fee. However, this is a limited-period offer.',
	},
	{
		question: 'Q. What is the fee applicable for add-on cards?',
		answer:
			"You can get up to 3 lifetime free 'Add-on Credit Cards' issued to your family members. There's no annual fee applicable to add-on cards.",
	},
	{
		question:
			'Q. How do I link my Lifetime free ixigo AU Credit Card with UPI?',
		answer:
			'<div> <p style="margin: 0 0 5px 0"> <b> Here\'s how you can link your ixigo AU RuPay Credit Card with UPI:</b> </p>     <ul style="padding:0;margin:0">         <li style="list-style: disc ; margin-left: 12px">Open your UPI app and find the option to link your RuPay card. </li>         <li style="list-style: disc ; margin-left: 12px">Choose \'AU Small Finance Bank\' as your issuing bank.</li>         <li style="list-style: disc ; margin-left: 12px">The app will automatically fetch the credit card details linked to your mobile number.</li>         <li style="list-style: disc ; margin-left: 12px">Now, link the card and confirm.</li>    <li style="list-style: disc ; margin-left: 12px">Generate your UPI PIN to complete the process. </li>     </ul> </div>',
	},
	{
		question: 'Q. Can I link my ixigo AU Visa Credit Card with UPI?',
		answer:
			'<div> No, you can only link your <b> ixigo AU RuPay Credit Card </b> with UPI. </div>',
	},
	{
		question: 'Q. What are the benefits of ixigo AU Credit Card reward points?',
		answer:
			'<div>ixigo AU Credit Card enhances the banking experience for customers by awarding them reward points for transactions made with the card. They can earn reward points on all spends, including bookings made via ixigo and online & offline transactions (excluding fuel, rent, wallet, and jewellery).<br> <br>For every ₹200 spent on online transactions, including flight, bus, and hotel bookings on ixigo, customers can earn 5 reward points. And for every ₹200 spent on offline transactions, they earn 5 reward points, making it one of the best travel credit cards for frequent travellers.<br><br>The accumulated reward points can be redeemed for ixigo money vouchers to get instant discounts on the ixigo platform. These points can also be redeemed for various products and services, including e-gift cards, merchandise, cinema tickets, recharges, and more from a range of online and offline merchants.<br><br>To view your reward points balance, redeem the reward points or learn more about the program, you can access the \'Card Management\' flow on the ixigo app. Alternatively, you can visit  <a style="color: blue ; text-decoration: underline" target="_blank" href="https://rewardz.aubank.in/">https://rewardz.aubank.in/</a>.</div>',
	},
	{
		question:
			'Q. Can I earn reward points on the UPI transactions via the Lifetime Free RuPay Credit Card?',
		answer:
			'Yes, you will earn 5 reward points for every ₹200 spent on offline UPI transactions and 5 reward points for every ₹200 spent on online UPI transactions. However, please ensure your card is linked with UPI to earn these reward points.',
	},
	{
		question: 'Q. What is the value of 1 reward point?',
		answer:
			'The value of 1 reward point is 25 paise across all categories, except e-vouchers, where 1 reward point is equivalent to 20 paise. The value of 1 reward point is 50 paise on ixigo money redemption.',
	},
	{
		question:
			'Q. When do reward points get credited to my card account after making a transaction?',
		answer:
			'<div>Once the transaction is posted in your card account, the eligible reward points are credited within 5 working days. To check your latest reward points balance, please visit the \'Card management\' section on the ixigo app or visit <a style="color: blue ; text-decoration: underline" target="_blank" href="https://rewardz.aubank.in/">https://rewardz.aubank.in/</a>.</div>',
	},
	{
		question:
			'Q. Am I eligible for reward points or cashback on all types of transactions?',
		answer:
			'<div>You will earn reward points or cashback only for POS and online retail transactions, excluding those related to fuel, wallets, jewellery, rent, and insurance. Reward points can also be earned on UPI transactions done via the travel Credit Card.<br><br>Please note that any other transactions such as balance transfers, EMIs, cash withdrawals and charges, among others, are not considered for accruing cashback or reward points. </div>',
	},
	{
		question:
			'Q. Is there any capping or limit on reward points on the ixigo AU Credit Card?',
		answer:
			'<div><p style="margin: 0 0 5px 0">Yes, you can earn a maximum of 10,000 reward points per month on the ixigo AU Credit Card. This makes it one of the best travel credit cards in India.</p><p style="margin: 5px 0">There are also certain restrictions on reward points in specific categories:</p><ul style="padding: 0; margin: 0"><li style="list-style: disc; margin-left: 12px">0 Reward Points on:</li><ul style="padding-left: 24px; margin: 0"><li style="list-style: circle; margin-left: 12px">Education & Government-related transactions</li><li style="list-style: circle; margin-left: 12px">BBPS & Rent transactions</li><li style="list-style: circle; margin-left: 12px">International transactions</li></ul><li style="list-style: disc; margin-left: 12px">1 Reward Point per ₹100 spent on:</li><ul style="padding-left: 24px; margin: 0"><li style="list-style: circle; margin-left: 12px">Telecom & Utilities transactions</li><li style="list-style: circle; margin-left: 12px">Insurance transactions (with a capping of 100 reward points per transaction)</li></ul></ul></div>',
	},
];
